/**
 * The timed pair's header (pedagogy spec §7.3.6, E6): "Tid kvar: {time}" above the session, with
 * the line that says how long the two problems get. The live region announces only at five minutes
 * and at zero, never the ticking digits (UX-01, like the exam's countdown). At zero a message
 * appears and nothing is submitted: the open flow (Klar, rubric, Spara) is the student's.
 *
 * The clock is `timed.startedAt + timed.limitSec`, so a refresh keeps it. No elapsed time is
 * stored anywhere (§13 Q28).
 */
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useT } from "@client/i18n";
import { countdownLevel, crossedThreshold } from "@client/lib/countdown";
import { formatDuration } from "@client/lib/format";
import type { TimedClock } from "../session";

const FIVE_MIN_MS = 5 * 60_000;
/** The two moments that are announced: five minutes left, and the clock reaching zero. */
const ANNOUNCED_MS = [FIVE_MIN_MS, 0] as const;
/** How long the five-minute line stays in the live region before it is cleared. */
const ANNOUNCEMENT_MS = 8_000;

export interface TimedHeaderProps {
  timed: TimedClock;
}

export function TimedHeader({ timed }: TimedHeaderProps) {
  const t = useT();
  const deadline = timed.startedAt + timed.limitSec * 1000;
  const [remainingMs, setRemainingMs] = useState(() => deadline - Date.now());
  const [fiveLeft, setFiveLeft] = useState(false);
  const previous = useRef(remainingMs);

  useEffect(() => {
    function tick() {
      const next = deadline - Date.now();
      const crossed = crossedThreshold(previous.current, next, ANNOUNCED_MS);
      previous.current = next;
      setRemainingMs(next);
      if (crossed === FIVE_MIN_MS) setFiveLeft(true);
      if (next <= 0) window.clearInterval(id);
    }
    const id = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(id);
  }, [deadline]);

  // The five-minute line is spoken once and then cleared, so it never sits stale in the region.
  useEffect(() => {
    if (!fiveLeft) return;
    const id = window.setTimeout(() => setFiveLeft(false), ANNOUNCEMENT_MS);
    return () => window.clearTimeout(id);
  }, [fiveLeft]);

  const level = countdownLevel(remainingMs);
  const over = level === "over";
  const message = over ? t("practice.timed.over") : fiveLeft ? t("practice.timed.fiveLeft") : "";

  return (
    <div className="surface mb-4 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-sm text-ink-700 dark:text-ink-200">
          {timed.problems && timed.problems !== 2
            ? t("practice.timed.introCount", {
                minutes: Math.round(timed.limitSec / 60),
                count: timed.problems,
              })
            : t("practice.timed.intro", { minutes: Math.round(timed.limitSec / 60) })}
        </p>
        <p
          role="timer"
          className={clsx(
            "font-display text-lg",
            level === "over" || level === "warn5" || level === "warn1"
              ? "text-red-700 dark:text-red-400"
              : level === "warn30"
                ? "text-amber-700 dark:text-amber-400"
                : "text-ink-900 dark:text-ink-50",
          )}
        >
          {t("practice.timed.remaining", { time: formatDuration(Math.max(0, remainingMs)) })}
        </p>
      </div>
      {/* Always mounted, starting empty: a status that appears already holding text is dropped by some readers. */}
      <p
        role="status"
        className={clsx(
          over
            ? "mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-900 dark:bg-red-950/40 dark:text-red-200"
            : "sr-only",
        )}
      >
        {message}
      </p>
    </div>
  );
}
